/**
 * APTableCell Component
 *
 * Individual table cell component that handles content rendering, alignment, and custom formatters.
 * Extracted from the main APTable component for better modularity and reusability.
 *
 * @example
 * ```tsx
 * <APTableCell
 *   column={{
 *     key: 'name',
 *     label: 'Name',
 *     accessor: 'name',
 *     align: 'left',
 *     render: (value) => <strong>{value}</strong>
 *   }}
 *   row={{ id: 1, data: { name: 'John Doe' } }}
 *   rowIndex={0}
 *   value="John Doe"
 * />
 * ```
 */

import { forwardRef } from 'react';

import type { IAPTableCellProps } from '../../types';

import './styles.scss';

// Helper function to get nested property value
const getNestedValue = <TData,>(
  obj: TData,
  path: keyof TData | ((row: TData) => unknown)
): unknown => {
  if (typeof path === 'function') {
    return path(obj);
  }
  return obj[path];
};

/**
 * APTableCell - Individual table cell component
 *
 * Handles content rendering with custom formatters, alignment variants,
 * and proper styling according to the design system.
 */
export const APTableCell = forwardRef<HTMLTableCellElement, IAPTableCellProps>(
  <TData,>(
    {
      column,
      row,
      rowIndex,
      value,
      className = '',
      'data-testid': testId = 'ap-table-cell',
      ...rest
    }: IAPTableCellProps<TData> & { 'data-testid'?: string },
    ref: React.Ref<HTMLTableCellElement>
  ) => {
    // Calculate cell value if not provided
    const cellValue = value !== undefined ? value : getNestedValue(row.data, column.accessor);

    // Build cell classes
    const cellClasses = [
      'ap-table__cell',
      `ap-table__cell--${column.align || 'left'}`,
      column.className,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    // Render cell content using custom renderer or default string conversion
    const cellContent = column.render
      ? column.render(cellValue, row.data, rowIndex)
      : String(cellValue || '');

    return (
      <td
        ref={ref}
        className={cellClasses}
        style={{
          width: column.width,
          minWidth: column.minWidth,
          maxWidth: column.maxWidth,
        }}
        data-testid={testId}
        {...rest}
      >
        <div className="ap-table__cell-content">{cellContent}</div>
      </td>
    );
  }
) as <TData = Record<string, unknown>>(
  props: IAPTableCellProps<TData> & {
    ref?: React.Ref<HTMLTableCellElement>;
    'data-testid'?: string;
  }
) => React.ReactElement;

export default APTableCell;
